import { useState } from "react";
import { WarningCircle, ArrowSquareOut, CaretDown, CaretUp } from "@phosphor-icons/react";

/**
 * RenewalWarningBanner — shows on /org when the tenant's subscription is flagged
 * `needs_manual_renewal` (i.e. Tap Save Card feature is disabled on the merchant
 * account and the last plan checkout had to fall back to a one-time charge).
 *
 * It includes an inline how-to for enabling Save Card in the Tap merchant dashboard,
 * so the org owner can fix it without contacting support.
 */
export default function RenewalWarningBanner({ tenant }) {
  const [expanded, setExpanded] = useState(false);
  const sub = tenant?.subscription;
  if (!sub?.needs_manual_renewal) return null;

  return (
    <section
      className="mt-6 border-2 border-amber-500 bg-amber-50"
      data-testid="renewal-warning-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <WarningCircle
          size={22}
          weight="fill"
          className="text-amber-600 shrink-0 mt-0.5"
        />
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-amber-900">
            / BILLING · RECURRING PAYMENTS OFF
          </div>
          <h3 className="font-display font-black text-xl mt-1 text-zinc-950">
            Your subscription is on a one-time charge — auto-renewals are OFF.
          </h3>
          <p className="text-sm text-zinc-700 mt-2 max-w-3xl">
            Tap Payments blocked the recurring setup (error 1108 — <em>Save Card
            is not enabled on your Tap merchant account</em>). Your last plan
            payment went through as a one-time charge, so nothing will auto-renew
            next month. Enable Save Card / RECURRING on the Tap dashboard and
            the next checkout will store the card and set up renewals automatically.
          </p>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="renewal-banner-toggle"
            className="mt-3 inline-flex items-center gap-1 text-xs font-mono tracking-[0.12em] uppercase text-amber-900 hover:text-zinc-950"
          >
            {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {expanded ? "HIDE STEPS" : "SHOW ME HOW TO FIX"}
          </button>

          {expanded && (
            <ol
              className="mt-3 space-y-2 text-sm text-zinc-800 list-decimal ml-5"
              data-testid="renewal-banner-steps"
            >
              <li>
                Sign in at{" "}
                <a
                  href="https://business.tap.company/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF3E00] font-semibold underline inline-flex items-center gap-1"
                >
                  business.tap.company <ArrowSquareOut size={11} />
                </a>{" "}
                with the merchant owner account.
              </li>
              <li>
                Go to <strong>Developers → Features</strong> (or{" "}
                <strong>Settings → Products</strong> on legacy dashboards).
              </li>
              <li>
                Toggle <strong>Save Card</strong> and{" "}
                <strong>Recurring Payments</strong> to <em>ON</em>. If they are
                greyed out, use the <em>Request activation</em> button — Tap
                usually enables it within a few hours for a test / bundle
                merchant.
              </li>
              <li>
                Once active, click the button below to re-attempt the
                subscription — the next payment will store the card and turn
                renewals back on automatically.
              </li>
            </ol>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/org#plans"
              data-testid="renewal-banner-retry-btn"
              className="inline-flex items-center gap-1 bg-zinc-950 text-white text-xs font-mono tracking-[0.15em] uppercase px-4 py-2 hover:bg-[#FF3E00] transition-colors"
            >
              RE-TRY SUBSCRIPTION →
            </a>
            <a
              href="mailto:support@smarterp-st.com?subject=Tap%20Save%20Card%20activation"
              data-testid="renewal-banner-contact-btn"
              className="inline-flex items-center gap-1 border border-zinc-950 text-xs font-mono tracking-[0.15em] uppercase px-4 py-2 hover:bg-zinc-950 hover:text-white transition-colors"
            >
              CONTACT SUPPORT
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
